import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, RotateCcw, Search } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/hooks";

const STATUS = { pending: "Finding", finding: "Finding", verifying: "Verifying", done: "Done", not_sourced: "Not sourced — conceptual", failed: "Couldn't source this item" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ProductCard = ({ p, type, selected, recommended, actions, testid }) => (
  <article data-testid={testid} className={`flex gap-4 rounded-xl border p-3 transition-colors duration-300 sm:p-4 ${selected ? "border-[var(--charcoal)] bg-white/70" : "border-[var(--line)] bg-white/35"}`}>
    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-white sm:h-28 sm:w-28">
      {p.imageUrl ? <img src={assetUrl(p.imageUrl)} alt={p.name} className="h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center text-[0.6rem] text-[var(--stone-dark)]">Image unknown</div>}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.08em] text-[var(--stone-dark)]">
        <span className="rounded-full bg-[var(--sand)] px-2 py-0.5" data-testid={`${testid}-type`}>{type}</span>
        <span data-testid={`${testid}-category`}>{p.category}</span>
        {recommended && <span className="text-[var(--accent)]">Recommended</span>}
        {selected && <span className="text-[var(--charcoal)]">Selected</span>}
      </div>
      <div className="mt-1 text-xs text-[var(--ink-soft)]" data-testid={`${testid}-manufacturer`}>{p.manufacturer}</div>
      <div className="font-display text-lg leading-tight" data-testid={`${testid}-name`}>{p.name}</div>
      <div className="font-mono-ui text-[0.7rem]" data-testid={`${testid}-sku`}>Model {p.sku}</div>
      {p.keySpecs?.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[0.72rem] text-[var(--ink-soft)]" data-testid={`${testid}-specs`}>
          {p.keySpecs.slice(0, 4).map((s) => <li key={s.name}>{s.name}: {s.value} <a href={s.sourceUrl} target="_blank" rel="noreferrer noopener" className="text-[var(--stone-dark)] underline">src</a></li>)}
        </ul>
      )}
      <div className="mt-1.5 flex flex-wrap gap-x-3 text-xs">
        <span data-testid={`${testid}-price`} className={p.price != null ? "font-medium" : "text-[var(--stone-dark)]"}>{p.price != null ? `Verified ${money(p.price)} · checked ${fmtDate(p.priceVerifiedAt)}` : "Price unknown"}</span>
        <span data-testid={`${testid}-availability`} className="text-[var(--stone-dark)]">{p.availabilityLabel}</span>
      </div>
      <a href={p.sourceUrl} target="_blank" rel="noreferrer noopener" className="btn-quiet !min-h-[36px] text-xs" data-testid={`${testid}-source-link`}>{p.sourceDomain} <ExternalLink size={12} /></a>
      {actions}
    </div>
  </article>
);

const Checks = ({ checks, label }) => (checks?.length ? (
  <div className="mt-3 rounded-xl bg-[var(--bone)] p-3" data-testid="compat-checks">
    <ul className="space-y-1 text-xs">
      {checks.map((c, i) => (
        <li key={i} className="flex gap-2" data-testid={`compat-${c.status}`}>
          <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${c.status === "pass" ? "bg-[#0F3D2E]" : c.status === "fail" ? "bg-[#B42318]" : c.status === "warning" ? "bg-[var(--accent)]" : "bg-[var(--stone)]"}`} />
          <span><b className="font-medium">{c.name}:</b> {c.message}</span>
        </li>
      ))}
    </ul>
    <p className="mt-2 text-[0.65rem] text-[var(--stone-dark)]" data-testid="compat-label">{label}</p>
  </div>
) : null);

const LineItem = ({ it, label, act }) => {
  const [replace, setReplace] = useState(false);
  const busy = ["pending", "finding", "verifying"].includes(it.status);
  const type = it.procurementType;
  return (
    <li className="border-t border-[var(--line)] py-6" data-testid={`line-item-${it.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-[var(--charcoal)]/30 px-2 py-0.5 font-mono-ui text-[0.6rem] uppercase tracking-[0.08em]" data-testid={`line-item-type-${it.id}`}>{type}</span>
            {it.category && <span className="text-[0.7rem] text-[var(--stone-dark)]" data-testid={`line-item-category-${it.id}`}>{it.category}</span>}
          </div>
          <h4 className="mt-1 font-display text-xl">{it.name}</h4>
        </div>
        <span className="flex items-center gap-1.5 text-xs" data-testid={`line-item-status-${it.id}`}>{busy && <Loader2 size={13} className="animate-spin" />}{type === "Service" ? "Quote required" : it.conceptualKept && it.status === "not_sourced" ? "Conceptual" : STATUS[it.status]}</span>
      </div>
      {type === "System" && <p className="mt-2 text-xs text-[var(--ink-soft)]" data-testid={`line-item-components-${it.id}`}>Components: {it.components.join(", ")}</p>}
      {type === "Service" && <p className="mt-2 text-xs text-[var(--ink-soft)]" data-testid={`service-concern-${it.id}`}>{it.concern ? `Addresses: ${it.concern}` : it.description} · Quote required · no product, no price</p>}
      {it.status === "failed" && <button className="btn-primary mt-3 !min-h-[44px]" onClick={() => act("post", `/line-items/${it.id}/retry`)} data-testid={`line-item-retry-${it.id}`}><RotateCcw size={14} /> Try again</button>}
      {type !== "Service" && it.products?.length > 0 && (
        <div className="mt-3 grid gap-3">
          {it.products.filter((p, i) => replace || p.id === (it.selectedProductId || it.recommendedId) || (i === 0 && !it.selectedProductId)).map((p) => (
            <ProductCard key={p.id} p={p} type={type} testid={`product-card-${p.id}`} selected={p.id === it.selectedProductId} recommended={p.id === it.recommendedId}
              actions={
                <div className="mt-2 flex flex-wrap gap-2">
                  {p.id !== it.selectedProductId && <button className="btn-primary !min-h-[44px] !px-4 text-xs" onClick={() => { act("post", `/line-items/${it.id}/accept`, { productId: p.id }); setReplace(false); }} data-testid={`accept-${it.id}-${p.id}`}>Accept</button>}
                </div>
              } />
          ))}
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost !min-h-[44px] !px-4 text-xs" onClick={() => setReplace((r) => !r)} data-testid={`replace-${it.id}`}>{replace ? "Hide alternatives" : "Replace"}</button>
            {replace && <button className="btn-ghost !min-h-[44px] !px-4 text-xs" disabled={busy} onClick={() => act("post", `/line-items/${it.id}/find-another`)} data-testid={`find-another-${it.id}`}><Search size={13} /> Find another</button>}
            <button className="btn-quiet text-xs" onClick={() => act("post", `/line-items/${it.id}/remove`)} data-testid={`remove-${it.id}`}>Remove</button>
          </div>
        </div>
      )}
      {type !== "Service" && it.status === "not_sourced" && <button className="btn-quiet text-xs" onClick={() => act("post", `/line-items/${it.id}/remove`)} data-testid={`remove-${it.id}`}>Remove</button>}
      {type !== "Service" && <Checks checks={it.compatibility} label={label} />}
    </li>
  );
};

const Measurements = ({ view, reload }) => {
  const [vals, setVals] = useState({});
  const room = view.room || {};
  const keys = [...(room.missingMeasurements || []), ...Object.keys(room.measurements || {}).map((k) => ({ key: k, label: view.measurementLabels?.[k] || k }))];
  if (!keys.length || !room.id) return null;
  const save = async (k) => {
    try { await api.put(`/rooms/${room.id}/measurements`, { key: k, value: vals[k] ? Number(vals[k]) : null }); await reload(); toast("Measurement saved — checks re-run"); } catch (e) { toast(errMsg(e, "Couldn't save")); }
  };
  return (
    <div className="mt-6 rounded-xl bg-[var(--bone-2)] p-4 sm:p-5" data-testid="missing-measurements">
      <div className="eyebrow mb-1">Missing measurements · {room.name}</div>
      <p className="mb-3 text-xs text-[var(--stone-dark)]">Optional. Enter what you've measured on site (inches; floor area in sq ft) to run the rule checks.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {keys.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2">
            <label className="min-w-0 flex-1 text-xs">{label}{room.measurements?.[key] ? ` · ${room.measurements[key].value} ${room.measurements[key].unit}` : ""}</label>
            <input inputMode="decimal" className="field !min-h-[44px] !w-24" value={vals[key] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))} data-testid={`measurement-input-${key}`} />
            <button className="btn-primary !min-h-[44px] !px-3 text-xs" onClick={() => save(key)} data-testid={`measurement-save-${key}`}>Save</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SelectedProducts = ({ products }) => (products?.length ? (
  <section className="mt-12" data-testid="selected-products">
    <div className="eyebrow mb-4">Selected real products</div>
    <div className="grid gap-3 md:grid-cols-2">{products.map((p) => <ProductCard key={p.id} p={p} type={p.lineItem} testid={`selected-product-${p.id}`} />)}</div>
  </section>
) : null);

export const PublicProducts = ({ visionId }) => {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/visions/${visionId}/sourcing`).then((r) => setList(r.data.selectedProducts || [])).catch(() => setList([])); }, [visionId]);
  return <SelectedProducts products={list} />;
};

export const SourcingPanel = ({ visionId, canStart, onGenerated }) => {
  const [view, setView] = useState(null);
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/visions/${visionId}/sourcing`).then((r) => { setView(r.data); return r.data; }), [visionId]);
  useEffect(() => { load().then((d) => d?.budget && setBudget(String(d.budget))); }, [load]);
  const running = view?.job && (["planning", "running"].includes(view.job.status) || view.items?.some((i) => ["pending", "finding", "verifying"].includes(i.status)));
  usePoll(load, !!running, 3000);
  const act = async (method, url, body) => {
    setBusy(true);
    try { const { data } = await api[method](url, body); if (data?.items) setView(data); else await load(); } catch (e) { toast(errMsg(e, "Something went wrong. Try again.")); }
    setBusy(false);
  };
  const generate = async () => {
    setBusy(true);
    try { await api.post(`/visions/${visionId}/grounded-generate`); onGenerated(); toast("Generating with your selected products"); } catch (e) { toast(errMsg(e, "Couldn't start generation")); }
    setBusy(false);
  };
  if (!view) return null;
  const items = (view.items || []).filter((i) => !i.removed);
  const start = (
    <button className="btn-primary" disabled={!canStart || busy || running} onClick={() => act("post", `/visions/${visionId}/sourcing`)} data-testid="source-products-button">
      {(busy || running) && <Loader2 size={15} className="animate-spin" />} {view.job ? "Source again" : "Source real products"}
    </button>
  );
  return (
    <section className="mt-16" data-testid="sourcing-section">
      <div className="eyebrow mb-2">PlaceBacker It</div>
      <h2 className="font-display text-3xl tracking-tight">Real products for this vision</h2>
      <p className="mt-2 max-w-2xl text-sm text-[var(--ink-soft)]">We search, then check each product against its live page. Only items whose manufacturer and exact model appear on that page are shown — everything else stays Conceptual.</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">{start}</div>
      {view.job?.status === "failed" && (
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-[#B42318]/30 bg-[#FDECEA] px-4 py-3" data-testid="sourcing-failed">
          <span className="font-display text-lg">Sourcing didn&apos;t finish — the vision stays Conceptual.</span>
          <button className="btn-primary !min-h-[44px]" onClick={() => act("post", `/visions/${visionId}/sourcing`)} data-testid="sourcing-retry-button"><RotateCcw size={14} /> Try again</button>
        </div>
      )}
      {view.job?.status === "planning" && <div className="mt-5 flex items-center gap-2 text-sm" data-testid="sourcing-planning"><Loader2 size={15} className="animate-spin" /> Reading the vision and listing what it needs…</div>}
      {items.length > 0 && (
        <>
          <div className="sticky top-14 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[var(--charcoal)] px-4 py-3 text-[var(--bone)]" data-testid="sourcing-totals">
            <div className="text-sm"><span data-testid="verified-total">{view.totals.verifiedTotal > 0 ? `Verified total ${money(view.totals.verifiedTotal)}` : "No verified prices yet"}</span> · <span data-testid="unknown-price-count">{view.totals.unknownCount} items with unknown price</span></div>
            <div className="text-xs" data-testid="grounding-preview">{view.grounding.status}{view.grounding.note ? ` · ${view.grounding.note}` : ""}</div>
          </div>
          <form className="mt-4 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); act("put", `/visions/${visionId}/sourcing/budget`, { budget: budget ? Number(String(budget).replace(/[^0-9.]/g, "")) : null }); }}>
            <label className="eyebrow shrink-0">Budget</label>
            <input className="field !min-h-[44px] max-w-[10rem]" inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="25000" data-testid="sourcing-budget-input" />
            <button className="btn-ghost !min-h-[44px] !px-4 text-xs" disabled={busy} data-testid="sourcing-budget-save">Re-rank</button>
          </form>
          <ul className="mt-4" data-testid="line-items-list">{items.map((it) => <LineItem key={it.id} it={it} label={view.compatLabel} act={act} />)}</ul>
          <Measurements view={view} reload={load} />
          <div className="mt-6 flex flex-wrap gap-2">
            <button className="btn-ghost" disabled={busy} onClick={() => act("post", `/visions/${visionId}/sourcing/continue-conceptual`)} data-testid="continue-conceptual-button">Continue with conceptual elements</button>
            <button className="btn-primary" disabled={busy || !view.grounding.selected} onClick={generate} data-testid="generate-with-products-button">Generate with these products</button>
          </div>
        </>
      )}
      {view.job && <SelectedProducts products={view.selectedProducts} />}
    </section>
  );
};
